import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ApiService } from '../../../core/services/api.service';
import { AuthService } from '../../../core/services/auth.service';
import { CenterService } from '../../../core/services/center.service';
import { phoneValidator, pincodeValidator } from '../../../shared/validators/custom-validators';
import { forkJoin, Subscription } from 'rxjs';
import { PageTitleService } from '../../../core/services/page-title.service';
import { MemberListPanelComponent } from '../../../shared/components/member-list-panel/member-list-panel.component';
import { DateFormatService } from '../../../core/services/date-format.service';

@Component({
  selector: 'app-member-master',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MemberListPanelComponent],
  templateUrl: './member-master.component.html',
  styleUrl: './member-master.component.scss',
})
export class MemberMasterComponent implements OnInit {
  view: 'list' | 'form' = 'list';
  private isAddMemberRoute = false;

  // Form state
  form!: FormGroup;
  isEditMode = false;
  editId: string | null = null;
  editOriginalMemberType: string | null = null;
  isSaving = false;
  successMessage = '';
  errorMessage = '';

  isAdmin = false;
  isManager = false;
  isHomeDelivery = false;

  // Routes and areas — loaded for form dropdowns
  routes: any[] = [];
  memberAreas: string[] = [];
  extraAreas: string[] = [];

  get uniqueAreas(): string[] { return this.memberAreas; }

  // Add-area popup
  showAddArea = false;
  newAreaName = '';

  // Plan / deposit display (shared between add and edit modes)
  subscriptionPlans: any[] = [];
  configDepositAmount = 0;
  newMemberDurationMonths = 12;
  newMemberSubscriptionAmount: number | null = null;
  editDisplayFee: number | null = null;
  editDisplayDeposit = 0;
  editDisplaySubscriptionType = '';

  get displayFee(): number | null {
    return this.isEditMode ? this.editDisplayFee : this.newMemberSubscriptionAmount;
  }
  get displayDeposit(): number {
    return this.isEditMode ? this.editDisplayDeposit : this.configDepositAmount;
  }

  // New-member payment details
  newMemberPaymentMode = 'Cash';
  newMemberPaymentTrnxRef = '';
  newMemberPaymentDate = '';
  newMemberPaymentRemarks = '';

  editMembershipExpiresOn = '';
  /** Set when opened via Edit/Add from a dashboard drill-down; Back/Save returns there. */
  private returnToStatDetail: { type: string; title: string; memberType?: string; locationId?: string } | null = null;

  get computedExpiry(): string {
    if (this.isEditMode) {
      if (!this.editMembershipExpiresOn) return '—';
      return this.dateFmt.format(new Date(this.editMembershipExpiresOn));
    }
    const since = this.form.get('memberSince')?.value;
    const months = Number(this.form.get('membershipDurationMonths')?.value || 0);
    if (!since || !months) return '—';
    const d = new Date(since);
    d.setMonth(d.getMonth() + months);
    return this.dateFmt.format(d);
  }

  readonly paymentModes = ['UPI', 'Cash', 'Chq', 'Bank Transfer', 'Other'];

  readonly memberTypes = [
    { value: 'A', label: 'Across the Counter' },
    { value: 'H', label: 'Home Delivery' },
  ];

  private memberTypeSub?: Subscription;

  constructor(
    private fb: FormBuilder,
    private api: ApiService,
    private auth: AuthService,
    private centerSvc: CenterService,
    private pageTitle: PageTitleService,
    private router: Router,
    private dateFmt: DateFormatService,
  ) { }

  ngOnInit(): void {
    const user = this.auth.getCurrentUser();
    this.isAdmin = user?.role?.toLowerCase() === 'admin';
    this.isManager = user?.role?.toLowerCase() === 'manager';
    const subtitle = this.isManager
      ? `Members at ${this.centerSvc.selectedCenter?.centerName || 'your center'}`
      : 'Manage library members';
    this.pageTitle.set('Member Master', subtitle);
    this.isAddMemberRoute = this.router.url.startsWith('/add-member');
    this.buildForm();
    const state = history.state || {};
    this.returnToStatDetail = state.returnToStatDetail || null;
    if (this.isAddMemberRoute || state.openAdd) {
      this.addNew();
    } else if (state.editMember) {
      this.editRecord(state.editMember);
    }
    // Consume the one-shot navigation state so a browser refresh shows the
    // plain list instead of reopening the form.
    if (state.openAdd || state.editMember || state.returnToStatDetail) {
      history.replaceState({ ...state, openAdd: undefined, editMember: undefined, returnToStatDetail: undefined }, '');
    }
  }

  get selectedCenter() { return this.centerSvc.selectedCenter; }

  /** Loads routes (for Home Delivery dropdown) and areas (for area dropdown). Only needed once the form is open. */
  loadFormData(): void {
    forkJoin({ routes: this.api.getRouteMasters(), areas: this.api.getMemberAreas() }).subscribe({
      next: ({ routes, areas }) => {
        this.routes = routes || [];
        this.memberAreas = [...new Set([...(areas || []), ...this.extraAreas])].sort() as string[];
      },
      error: () => {},
    });
  }

  buildForm(): void {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
      phone: ['', [Validators.required, phoneValidator()]],
      phone1wa: [false],
      phone2: ['', [phoneValidator()]],
      phone2wa: [false],
      area: ['', [Validators.maxLength(100)]],
      landmark: ['', [Validators.maxLength(100)]],
      pincode: ['', [pincodeValidator()]],
      address: ['', [Validators.maxLength(300)]],
      memberSince: [this.todayString(), [Validators.required]],
      membershipDurationMonths: [12, [Validators.required, Validators.min(1), Validators.max(24)]],
      depositDate: [this.todayString()],
      maxBooksAllowed: [5, [Validators.required, Validators.min(1), Validators.max(20)]],
      memberType: ['A', [Validators.required]],
      locationId: [''],
      isActive: [true],
      status: ['MEMBER'],
      onHoldTill: [''],
    });

    this.memberTypeSub = this.form.get('memberType')!.valueChanges.subscribe(val => {
      this.isHomeDelivery = val === 'H';
      const locationCtrl = this.form.get('locationId')!;
      if (this.isHomeDelivery) {
        locationCtrl.setValidators([Validators.required]);
      } else {
        locationCtrl.clearValidators();
        locationCtrl.setValue('');
      }
      locationCtrl.updateValueAndValidity();
    });
  }

  todayString(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  addNew(): void {
    this.isEditMode = false;
    this.editId = null;
    this.isHomeDelivery = false;
    const today = this.todayString();
    this.form.reset({
      memberSince: today, membershipDurationMonths: 12, depositDate: today,
      maxBooksAllowed: 5, memberType: 'A', locationId: '', isActive: true,
      phone1wa: false, phone2: '', phone2wa: false, status: 'MEMBER', onHoldTill: '',
    });
    this.form.get('locationId')!.clearValidators();
    this.form.get('locationId')!.updateValueAndValidity();
    this.newMemberDurationMonths = 12;
    this.newMemberSubscriptionAmount = null;
    this.newMemberPaymentMode = 'Cash';
    this.newMemberPaymentTrnxRef = '';
    this.newMemberPaymentDate = today;
    this.newMemberPaymentRemarks = '';
    this.successMessage = '';
    this.errorMessage = '';
    this.view = 'form';
    this.pageTitle.set('Member Master', 'Register a new library member');
    this.loadFormData();
    forkJoin({
      settings: this.api.getGeneralSettings(),
      plans: this.api.getSubscriptionPlans(),
    }).subscribe({
      next: ({ settings, plans }) => {
        this.configDepositAmount = settings?.depositAmount ?? 0;
        this.subscriptionPlans = plans || [];
        this.onNewMemberPlanChange();
      },
      error: () => {},
    });
  }

  onNewMemberPlanChange(): void {
    const months = Number(this.form.get('membershipDurationMonths')?.value);
    const plan = this.subscriptionPlans.find(p => p.months === months);
    this.newMemberSubscriptionAmount = plan?.charges ?? null;
    this.newMemberDurationMonths = months;
  }

  editRecord(record: any): void {
    this.isEditMode = true;
    this.editId = record._id || record.id;
    this.successMessage = '';
    this.errorMessage = '';
    const mt = record._memberTypeRaw === 'H' ? 'H' : 'A';
    this.editOriginalMemberType = mt;
    this.isHomeDelivery = mt === 'H';
    const locationCtrl = this.form.get('locationId')!;
    if (this.isHomeDelivery) {
      locationCtrl.setValidators([Validators.required]);
    } else {
      locationCtrl.clearValidators();
    }
    locationCtrl.updateValueAndValidity();

    this.editDisplayFee        = record.subscriptionAmount ?? null;
    this.editDisplayDeposit    = record.depositAmount ?? 0;
    this.editDisplaySubscriptionType = record.subscriptionType || '';

    this.newMemberPaymentMode     = 'Cash';
    this.newMemberPaymentTrnxRef  = '';
    this.newMemberPaymentDate     = this.todayString();
    this.newMemberPaymentRemarks  = '';

    this.editMembershipExpiresOn = record.membershipExpiresOn || '';
    this.form.patchValue({
      name:                    record.name,
      phone:                   record.phone,
      phone1wa:                !!record.Phone1onwa,
      phone2:                  record.phone2 || '',
      phone2wa:                !!record.phone2onwa,
      area:                    record.area || '',
      landmark:                record.landmark || '',
      pincode:                 record.pincode || '',
      address:                 record.address || '',
      memberSince:             record.memberSince ? record.memberSince.split('T')[0] : this.todayString(),
      membershipDurationMonths: this._typeToMonths(record.subscriptionType),
      depositDate:             record.depositDate ? record.depositDate.split('T')[0] : this.todayString(),
      maxBooksAllowed:         record.maxBooksAllowed,
      memberType:              mt,
      locationId:              record.locationId || '',
      isActive:                record.isActive !== false,
      status:                  record.status || 'MEMBER',
      onHoldTill:              record.onHoldTill ? record.onHoldTill.split('T')[0] : '',
    });
    this.view = 'form';
    this.pageTitle.set('Member Master', 'Update member information');
    this.loadFormData();
  }

  private _typeToMonths(type: string): number {
    const map: Record<string, number> = {
      'Monthly': 1, 'Quarterly': 3, 'Half-Yearly': 6, 'Annual': 12, 'Bi-Annual': 24
    };
    return map[type] || 12;
  }

  openAddAreaDialog(): void {
    this.newAreaName = '';
    this.showAddArea = true;
  }

  confirmAddArea(): void {
    const area = this.newAreaName.trim();
    if (!area) return;
    if (!this.extraAreas.includes(area)) {
      this.extraAreas.push(area);
      this.extraAreas.sort();
    }
    this.memberAreas = [...new Set([...this.memberAreas, area])].sort() as string[];
    this.form.get('area')!.setValue(area);
    this.form.get('area')!.markAsTouched();
    this.newAreaName = '';
    this.showAddArea = false;
  }

  cancelAddArea(): void {
    this.newAreaName = '';
    this.showAddArea = false;
  }

  backToList(): void {
    if (this.returnToStatDetail) {
      // Opened from a dashboard drill-down — go back to that member list.
      this.router.navigate(['/dashboard'], { state: { reopenStatDetail: this.returnToStatDetail } });
      return;
    }
    if (this.isAddMemberRoute) {
      this.router.navigate(['/members']);
      return;
    }
    this.view = 'list';
    this.pageTitle.set('Member Master', 'Manage library members');
  }

  get f() { return this.form.controls; }

  isFieldInvalid(field: string): boolean {
    const ctrl = this.form.get(field);
    return !!(ctrl && ctrl.invalid && ctrl.touched);
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    if (this.isSaving) return;

    const payload = { ...this.form.value };

    if (this.isEditMode && this.editOriginalMemberType && payload.memberType !== this.editOriginalMemberType) {
      const fromLabel = this.memberTypes.find(t => t.value === this.editOriginalMemberType)?.label;
      const toLabel = this.memberTypes.find(t => t.value === payload.memberType)?.label;
      const ok = confirm(`This will move the member from "${fromLabel}" to "${toLabel}". The member number may be updated accordingly. Continue?`);
      if (!ok) return;
    }

    this.isSaving = true;
    this.successMessage = '';
    this.errorMessage = '';

    payload.Phone1onwa = payload.phone1wa ? payload.phone : '';
    payload.phone2onwa = payload.phone2wa ? (payload.phone2 || '') : '';
    delete payload.phone1wa;
    delete payload.phone2wa;

    const centerId = this.centerSvc.selectedCenter?._id || '';

    const request$ = this.isEditMode
      ? this.api.updateMember({ ...payload, id: this.editId, centerId })
      : this.api.saveMember({
          ...payload,
          centerId,
          depositAmount:      this.configDepositAmount,
          subscriptionAmount: this.newMemberSubscriptionAmount,
          paymentMode:        this.newMemberPaymentMode,
          paymentTrnxRef:     this.newMemberPaymentTrnxRef,
          paymentDate:        this.newMemberPaymentDate || payload.memberSince,
          paymentRemarks:     this.newMemberPaymentRemarks,
        });

    request$.subscribe({
      next: (res: any) => {
        this.isSaving = false;
        if (this.isEditMode && res?.memNo && res?.previousMemNo && res.memNo !== res.previousMemNo) {
          this.successMessage = `Member updated — number changed from ${res.previousMemNo} to ${res.memNo}.`;
        } else {
          this.successMessage = this.isEditMode ? 'Member updated successfully!' : 'Member saved successfully!';
        }
        setTimeout(() => this.backToList(), 1500);
      },
      error: (err: any) => {
        this.isSaving = false;
        this.errorMessage = err?.error?.message || 'Failed to save member.';
      },
    });
  }
}
